// src/services/auth.providers.js
// Helpers for the multi-provider auth model: Google + Apple OAuth, Firebase
// Phone Auth, and provider linking to an already-authenticated Firebase user.
// All flows converge on ONE Firebase UID per user — `linkWithCredential`
// attaches additional credentials so the user can sign in via any of them
// and still hit the same Polyp Users row.
//
// Browser nuance: Safari's ITP blocks signInWithPopup cookies, so we transparently
// fall back to signInWithRedirect on Safari. Pages must call
// `consumePendingRedirectResult()` once on mount to surface that flow's result.

import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  linkWithCredential,
  PhoneAuthProvider,
} from "firebase/auth";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { auth, googleProvider, appleProvider } from "../firebase";

const isMobileSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Safari on iOS / iPadOS reliably; also macOS Safari which is the desktop
  // case that breaks signInWithPopup with strict ITP defaults.
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return isSafari || isIOS;
};

// signInWithPopup fails silently on Safari when third-party cookies are blocked.
// Redirect path works everywhere but disrupts the SPA flow, so we only use it
// when popup is known to break.
async function popupOrRedirect(provider) {
  if (isMobileSafari()) {
    await signInWithRedirect(auth, provider);
    return null; // result arrives via consumePendingRedirectResult on next mount
  }
  return signInWithPopup(auth, provider);
}

export async function signInWithGoogle() {
  return popupOrRedirect(googleProvider);
}

export async function signInWithApple() {
  return popupOrRedirect(appleProvider);
}

// Pages that use redirect flow must call this on mount (e.g. in App.js or in
// the Login/Signup pages' useEffect). Returns the UserCredential or null.
export async function consumePendingRedirectResult() {
  try {
    return await getRedirectResult(auth);
  } catch (err) {
    console.error("getRedirectResult failed:", err);
    throw err;
  }
}

// ─── Firebase Phone Auth ──────────────────────────────────────────────────
//
// Two reCAPTCHA modes:
//   - invisible (default): rendered into an empty <div> — user sees nothing
//     unless Firebase decides a challenge is needed.
//   - visible: forces the widget for debugging or when invisible fails.
//
// Caller is responsible for placing a container element in the DOM with the
// id passed in `containerId`. A common pattern is a hidden `<div id="recaptcha-container" />`
// on the page that hosts the phone-verification form.

let _recaptchaVerifier = null;

export function getOrCreateRecaptcha(containerId, { visible = false } = {}) {
  if (_recaptchaVerifier) return _recaptchaVerifier;
  _recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: visible ? "normal" : "invisible",
    callback: () => {
      // reCAPTCHA solved — token will be passed automatically.
    },
    "expired-callback": () => {
      // Token expired — force re-render on next use.
      try { _recaptchaVerifier?.clear(); } catch {}
      _recaptchaVerifier = null;
    },
  });
  return _recaptchaVerifier;
}

export function resetRecaptcha() {
  try { _recaptchaVerifier?.clear(); } catch {}
  _recaptchaVerifier = null;
}

// Start a phone-number verification (sends an SMS via Firebase). Returns a
// ConfirmationResult — call `.confirm(code)` to complete sign-in, or extract
// `verificationId` to build a credential for linking via PhoneAuthProvider.
export async function startPhoneVerification(e164PhoneNumber, recaptchaContainerId) {
  const verifier = getOrCreateRecaptcha(recaptchaContainerId);
  return signInWithPhoneNumber(auth, e164PhoneNumber, verifier);
}

// Link a verified phone credential to the currently signed-in Firebase user.
// Used after social signup ("Continue with Google" → collect phone → link) and
// from Settings ("Enable phone sign-in" for existing accounts).
export async function linkPhoneToCurrentUser(confirmationResult, code) {
  if (!auth.currentUser) throw new Error("No signed-in user");
  const credential = PhoneAuthProvider.credential(
    confirmationResult.verificationId,
    code
  );
  return linkWithCredential(auth.currentUser, credential);
}

// ─── Provider linking from Settings ───────────────────────────────────────
//
// Caller drives the popup/redirect for the SECOND provider, then we link the
// resulting credential to the currently-authed user. Used by AccountSecurityPage
// "Link Google / Link Apple" controls.

export async function linkProviderToCurrentUser(providerName) {
  if (!auth.currentUser) throw new Error("No signed-in user");
  let provider;
  if (providerName === "google.com") provider = googleProvider;
  else if (providerName === "apple.com") provider = appleProvider;
  else throw new Error(`Unsupported provider: ${providerName}`);

  // signInWithPopup-based linking would re-auth the user; instead extract the
  // credential and call linkWithCredential. Firebase web SDK's recommended
  // pattern: linkWithPopup(currentUser, provider).
  const { linkWithPopup } = await import("firebase/auth");
  return linkWithPopup(auth.currentUser, provider);
}

// Normalize a phone number to E.164. Accepts any user-typed format and
// defaults the country to US when no +country code is present.
export function toE164(raw) {
  const parsed = parsePhoneNumberFromString(String(raw || ""), "US");
  if (parsed?.isValid()) return parsed.number;
  // Fallback: best-effort pass-through so callers that submit-then-validate
  // server-side don't lose the raw input on edge cases. Firebase will reject
  // invalid E.164 anyway.
  const digits = String(raw || "").replace(/\D/g, "");
  if (String(raw || "").startsWith("+")) return `+${digits}`;
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
}

// Convenience: list a user's linked providers.
export function listLinkedProviders(user = auth.currentUser) {
  if (!user) return [];
  return (user.providerData || []).map((p) => p.providerId);
}

const AuthProviders = {
  signInWithGoogle,
  signInWithApple,
  consumePendingRedirectResult,
  getOrCreateRecaptcha,
  resetRecaptcha,
  startPhoneVerification,
  linkPhoneToCurrentUser,
  linkProviderToCurrentUser,
  toE164,
  listLinkedProviders,
};

export default AuthProviders;
